import * as Yup from 'yup';
import states from './states.json';
import { map, prop, reduce } from 'ramda';
import { isEnabledFn, LoyaltyNumberAgeCheck, Tenant } from 'state/tenant';
import {
  isValid,
  startOfDay,
  isAfter,
  subDays,
  isBefore,
  subYears,
  compareAsc,
} from 'date-fns/fp';
import { parseDoB } from 'utils/dateUtils';

export const isName = Yup.string()
  .test(
    'noOnlyWhitespace',
    'Cannot be blank',
    value => !value || !/^\s+$/.test(value),
  )
  .test(
    'noDoubleSpaces',
    'Cannot contains double spaces',
    value => !value || !/\s{2}/.test(value),
  )
  .test(
    'noLeadingOrTrailingSpace',
    'Cannot begin or end with a space',
    value => !value || !/^[ \s]+|[ \s]+$/.test(value),
  )
  .test(
    'noOnlyHyphenOrApostrophe',
    'Cannot contain only hyphen or apostrophe',
    value => !value || !/^[-']+$/.test(value),
  )
  .test(
    'noDigit',
    'Cannot contain digits',
    value => !value || !/\d/.test(value),
  )
  .test(
    'noSymbol',
    // eslint-disable-next-line no-template-curly-in-string
    'Please enter a valid ${path}',
    value => !value || /^[a-zA-Z-' ]+$/.test(value),
  )
  .test(
    'noOnlyHyphenOrApostrophe',
    'Cannot contain only hyphen or apostrophe',
    value => !value || !/^[-']+$/.test(value),
  )
  .test(
    'noLeadingOrTrailingHyphen',
    'Cannot begin or end with a space',
    value => !value || !/^[ -]+|[ -]+$/.test(value),
  );

export const isMiddleInitial = Yup.string().test(
  'noLeadingOrTrailingSpace',
  'Cannot begin or end with a space',
  value => !value || !/^[ \s]+|[ \s]+$/.test(value),
);

export const isNotPoBox = Yup.string().test(
  'notPOBox',
  'Cannot be a P.O. box',
  (value: string) => {
    const pattern = /P[.\s]*O[.\s]+Box/gim;
    return !value || !value.match(pattern);
  },
);

export const isAddress = Yup.string()
  .test('noOnlyWhitespace', 'Cannot be blank', value => !/^\s+$/.test(value))
  .test(
    'noLeadingOrTrailingSpace',
    'Cannot begin or end with a space',
    value => !value || !/^[ \s]+|[ \s]+$/.test(value),
  )
  .test(
    'noSymbol',
    // eslint-disable-next-line no-template-curly-in-string
    'Please enter a valid ${path}',
    value => !value || /^[0-9a-zA-Z-&/()%.# ]+$/.test(value),
  )
  .test(
    'noSymbolOnly',
    'Cannot contain only symbols',
    value => !value || !/[-&/()%.# ]+$/.test(value),
  )
  .max(30, 'Cannot be longer then 30 characters');

export const isCity = Yup.string()
  .test(
    'noOnlyWhitespace',
    'Cannot be blank',
    value => !value || !/^\s+$/.test(value),
  )
  .max(20)
  .test(
    'noLeadingOrTrailingSpace',
    'Cannot begin or end with a space',
    value => !value || !/^[ \s]+|[ \s]+$/.test(value),
  )
  .test(
    'noDoubleSpaces',
    'Cannot contains double spaces',
    value => !value || !/\s{2}/.test(value),
  )
  .test(
    'noSymbol',
    // eslint-disable-next-line no-template-curly-in-string
    'Please enter a valid ${path}',
    value => !value || /^[a-zA-Z-'. ]+$/.test(value),
  )
  .test(
    'noSymbolOnly',
    'Cannot contain only symbols',
    value => !value || !/^[-'. ]+$/.test(value),
  );

export const isState = Yup.string()
  .transform(value => (typeof value === 'string' ? value.toUpperCase() : value))
  .oneOf(map(prop('abbreviation'), states), 'Must be a US state.');

export const isNotRestrictedState = (tenant: Tenant, errorMessage: string) =>
  Yup.string()
    .concat(isState)
    .test({
      name: 'notRestrictedState',
      exclusive: true,
      message: errorMessage,
      test: value => tenant.restrictedStates.indexOf(value) < 0,
    });

export const isZipCode = Yup.string()
  .matches(/^\d{5}$/, {
    message: 'Please enter your five-digit ZIP Code.',
    excludeEmptyString: true,
  })
  .notOneOf(['00000']);

export const isBeforeToday = (): Yup.StringSchema =>
  Yup.string()
    .test('isDate', 'Must be a valid date', (value: string) => {
      if (!value) return true;
      return isValid(parseDoB(value));
    })
    .test('beforeToday', `Must be before today`, (value: string) => {
      if (!value) return true;
      const parsed = parseDoB(value);
      return isBefore(startOfDay(new Date()), parsed);
    });

export const isBornAfter = (year: number): Yup.StringSchema =>
  Yup.string()
    .test('isDate', 'Must be a valid date', (value: string) => {
      if (!value) return true;
      return isValid(parseDoB(value));
    })
    .test('bornAfter', `Must be born on or after ${year}`, (value: string) => {
      if (!value) return true;
      const parsed = parseDoB(value);
      const date = subDays(1, new Date(year, 0, 1));
      return isAfter(date, parsed);
    });

export const isOtpCode = Yup.string().length(6);

/**
 * If LoyaltyNumberAgeCheck is an enabled feature: validate applicant age
 *
 * Applicants above the age threshhold are vaild if they a loyalty
 * number while not equaling the under age loyalty number
 *
 * Applicants below the age threshold are valid
 */
export const hasValidAge = (
  tenant: Tenant,
  loyaltyNumber: string | null,
  errorMessage: string,
): Yup.StringSchema =>
  Yup.string()
    .test('isDate', 'Must be a valid date', (value: string) => {
      if (!value) return true;
      return isValid(parseDoB(value));
    })
    .test('hasValidAge', errorMessage, (value: string) => {
      if (!isEnabledFn(tenant, 'loyaltyNumberAgeCheck')) return true;
      const loyaltyNumberAgeCheck =
        tenant.enabledFeatures.loyaltyNumberAgeCheck;
      const minimumAge = (loyaltyNumberAgeCheck as LoyaltyNumberAgeCheck)
        .minimumAge;
      const underAgeLoyaltyNumber = (loyaltyNumberAgeCheck as LoyaltyNumberAgeCheck)
        .underAgeLoyaltyNumber;

      const threshholdDoB = subYears(minimumAge, Date.now());
      const applicantDoB = parseDoB(value);
      const overAge = compareAsc(applicantDoB, threshholdDoB);

      if (!applicantDoB) return true;
      if (isNaN(applicantDoB.getTime())) return true;
      if (overAge >= 0) {
        return (
          Boolean(loyaltyNumber) && loyaltyNumber !== underAgeLoyaltyNumber
        );
      } else {
        return true;
      }
    });

export const isSsn = (isLastFour: boolean): Yup.StringSchema =>
  isLastFour
    ? Yup.string().matches(/^\d{4}$/, {
        message: 'Please enter the last four digits of your SSN',
        excludeEmptyString: true,
      })
    : Yup.string().matches(/^\d{9}$/, {
        message: 'Please enter your SSN',
        excludeEmptyString: true,
      });

export const isReferralCode = Yup.string().matches(
  /^[A-Za-z0-9]{0,10}$/,
  // The template string syntax is used by Yup for message.
  // eslint-disable-next-line no-template-curly-in-string
  '${path} is not valid',
);

export const isPhoneNumber = Yup.string()
  .transform((val: string) => (val ? val.replace(/\D/g, '') : null))
  .matches(/^[2-9]{1}[0-9]{9}$/, 'Please enter a valid mobile phone number');

export const isAlternatePhoneNumber = Yup.string()
  .transform((val: string) => (val ? val.replace(/\D/g, '') : null))
  .matches(/^[2-9]{1}[0-9]{9}$/, 'Please enter a valid phone number');

export const isMoney = Yup.string()
  .transform((val: string) => (val ? val.replace(/\D/g, '') : null))
  .matches(/^\d{0,7}$/, 'Cannot include more than 7 digits');

export const isDecimalMoney = Yup.string()
  .transform((val: string) => (val ? val.replace(/[^.|\d]/g, '') : null))
  .matches(/(^\d{0,7})(?=\.$)|^\d{0,7}/, 'Cannot include more than 7 digits');

export const isPrescreenId = Yup.string().matches(
  /^\d{12}$/,
  'Must be exactly 12 digits',
);

export const isEmail = Yup.string()
  .matches(/^[\S.]+@\S+\.+\S/, 'Must be a valid email address')
  .max(50);

export const isStoreNumber = Yup.string().max(5);

// By default loyaltyNumber should be at most 25, but we allow it to be
// configurable because some cases (MembershipValidation) only allow up to
// a different amount.
export const isLoyaltyNumber = (maxLength = 25) => Yup.string().max(maxLength);

/**
 * Only validate a subset of the given schema.
 */
export const partialSchema = <T extends object, K extends keyof T>(
  schema: Yup.ObjectSchema<T>,
  names: readonly K[],
): Yup.ObjectSchema<Pick<T, K>> => {
  const shape = names.reduce<Partial<Pick<T, K>>>(
    (conf, name) => ({
      ...conf,
      [name]: Yup.reach(schema, name.toString()),
    }),
    {},
  );

  return Yup.object((shape as unknown) as Yup.ObjectSchemaDefinition<
    Pick<T, K>
  >);
};

type ComposeSchema = <T>(
  schema: Yup.Schema<T>,
  ...schemas: (Yup.Schema<T> | null | undefined | false | 0 | '')[]
) => Yup.Schema<T>;

export const composeSchema: ComposeSchema = (schema, ...schemas) =>
  reduce(
    (schema, validation) => (validation ? schema.concat(validation) : schema),
    schema,
    schemas,
  );
